/** UI configuration for SEO landing pages — controls layout, CTAs, and section content. */

export type SeoLandingInternalRoute =
	| "/"
	| "/ai-teammates"
	| "/ai-teammates/marketing"
	| "/ai-teammates/marketing/google-ads"
	| "/ai-teammates/marketing/seo"
	| "/signup"
	| "/login"
	| "/pricing"
	| "/privacy-policy"
	| "/terms";

export type SeoLandingSectionId =
	| "value-props"
	| "onboarding"
	| "channel-module"
	| "real-examples"
	| "deep-dive"
	| "backlinks"
	| "faq"
	| "final-cta";

export type SeoLandingCtaVariant = "default" | "outline";

export interface SeoLandingBreadcrumbItem {
	label: string;
	to: SeoLandingInternalRoute;
}

export interface SeoLandingValueCard {
	title: string;
	description: string;
}

export interface SeoLandingStep {
	title: string;
	description: string;
}

export interface SeoLandingOnboardingStep {
	title: string;
	description: string;
}

export interface SeoLandingUseCase {
	title: string;
	scenario: string;
	exampleTask: string;
	outcome: string;
}

export interface SeoLandingCta {
	label: string;
	to: SeoLandingInternalRoute;
}

export interface SeoLandingHeroConfig {
	kicker: string;
	subhead: string;
	proofPoints: string[];
	primaryCta: SeoLandingCta;
	secondaryCta: SeoLandingCta;
}

export interface SeoLandingContextualCta {
	id: string;
	afterSectionId: SeoLandingSectionId;
	label: string;
	to: SeoLandingInternalRoute;
	supportingText: string;
	variant?: SeoLandingCtaVariant;
}

export interface SeoLandingSectionIntro {
	sectionId: SeoLandingSectionId;
	title: string;
	body: string;
}

export interface SeoLandingModuleCard {
	title: string;
	body: string;
	bullets: string[];
}

export interface SeoLandingPageModule {
	title: string;
	body: string;
	cards: SeoLandingModuleCard[];
}

export interface SeoLandingUiConfig {
	articleLabel: string;
	breadcrumbs: SeoLandingBreadcrumbItem[];
	/** Integration IDs from INTEGRATIONS registry to show as a logo strip. */
	integrationLogos?: string[];
	/** Collapse deep-dive sections after this index (0-based). Undefined = show all. */
	deepDiveCollapsedAfter?: number;
	hero: SeoLandingHeroConfig;
	contextualCtas: SeoLandingContextualCta[];
	sectionIntros: SeoLandingSectionIntro[];
	pageModule: SeoLandingPageModule;
	valueCards: SeoLandingValueCard[];
	steps: SeoLandingStep[];
	onboardingSteps: SeoLandingOnboardingStep[];
	useCases: SeoLandingUseCase[];
	deepDiveTitle: string;
	deepDiveIntro: string;
	finalCtaTitle: string;
	finalCtaBody: string;
	finalPrimaryCta: SeoLandingCta;
	finalSecondaryCta: SeoLandingCta;
}

/* ─── Hub page: /ai-teammates ─── */

export const AI_TEAMMATES_LANDING_UI: SeoLandingUiConfig = {
	articleLabel: "AI teammates SEO page",
	breadcrumbs: [
		{ label: "Home", to: "/" },
		{ label: "AI Teammates", to: "/ai-teammates" },
	],
	deepDiveCollapsedAfter: 2,
	hero: {
		kicker: "AI teammates",
		subhead:
			"You describe what needs doing. It does it. Monitoring, reporting, triage — on autopilot with human oversight.",
		proofPoints: [
			"Runs on schedule, every time",
			"Human approvals built in",
			"One workflow to full system",
		],
		primaryCta: { label: "Start free", to: "/signup" },
		secondaryCta: { label: "See pricing", to: "/pricing" },
	},
	contextualCtas: [
		{
			id: "value-model-launch",
			afterSectionId: "value-props",
			label: "Launch one workflow",
			to: "/signup",
			supportingText: "Start with your highest-friction recurring task. Validate in one cycle.",
		},
		{
			id: "use-case-marketing",
			afterSectionId: "real-examples",
			label: "See marketing workflows",
			to: "/ai-teammates/marketing",
			variant: "outline",
			supportingText: "Google Ads, SEO, Search Console, and lifecycle workflows ready to go.",
		},
		{
			id: "deep-dive-start",
			afterSectionId: "deep-dive",
			label: "Get started",
			to: "/signup",
			supportingText: "Ready to go? Create your workspace and launch your first teammate.",
		},
	],
	sectionIntros: [
		{
			sectionId: "value-props",
			title: "Not another AI chat. An actual operator.",
			body: "A teammate owns recurring work — monitoring, reporting, triage. Not a one-off prompt.",
		},
		{
			sectionId: "onboarding",
			title: "How it works",
			body: "Pick a task. Connect your tools. Let it run.",
		},
		{
			sectionId: "channel-module",
			title: "From help to actual ownership",
			body: "Most AI tools help when asked. This one owns the work and delivers on schedule.",
		},
		{
			sectionId: "real-examples",
			title: "Real use cases",
			body: "Pick one. Adapt it. Ship today.",
		},
		{
			sectionId: "deep-dive",
			title: "Implementation details",
			body: "The full breakdown — patterns, controls, and governance.",
		},
		{
			sectionId: "backlinks",
			title: "Learn more",
			body: "Related pages and reference material.",
		},
		{
			sectionId: "faq",
			title: "FAQ",
			body: "Quick answers to common questions.",
		},
		{
			sectionId: "final-cta",
			title: "Ready to ship your first teammate?",
			body: "Start narrow. Prove it works. Then scale.",
		},
	],
	pageModule: {
		title: "From help to actual ownership",
		body: "Most AI tools help when asked. This one owns the work and delivers on schedule.",
		cards: [
			{
				title: "Runs on cadence",
				body: "Recurring checks and summaries happen automatically.",
				bullets: [
					"Fixed schedule, no manual triggers",
					"Named owner for every workflow",
					"Clear handoff after each run",
				],
			},
			{
				title: "Built-in guardrails",
				body: "Stays inside explicit boundaries with human review gates.",
				bullets: [
					"Threshold-based escalation",
					"Scoped tool permissions",
					"Escalates uncertainty instead of guessing",
				],
			},
			{
				title: "Decision-ready output",
				body: "No raw data dumps. Concise synthesis with causes and next actions.",
				bullets: [
					"Consistent format every run",
					"Priority-ranked action queue",
					"Meeting-ready summaries",
				],
			},
		],
	},
	valueCards: [
		{
			title: "Always-on execution",
			description:
				"Recurring tasks become reliable runs. Fixed cadence, clear owners, structured output.",
		},
		{
			title: "Human control by default",
			description:
				"Scoped permissions, explicit thresholds, and approval gates. You stay in the loop.",
		},
		{
			title: "Fast time to value",
			description: "Launch one workflow, prove quality fast, expand only after it's stable.",
		},
	],
	steps: [
		{
			title: "Pick one high-friction task",
			description: "Choose a recurring task tied to revenue, pipeline, or delivery.",
		},
		{
			title: "Set the rules",
			description: "Define inputs, thresholds, output format, and who gets notified.",
		},
		{
			title: "Run and review",
			description: "Weekly review loop. Tune one variable at a time.",
		},
		{
			title: "Scale what works",
			description: "Expand only after repeated high-quality runs.",
		},
	],
	onboardingSteps: [
		{
			title: "Create one teammate",
			description: "Start with one outcome — weekly reporting, anomaly detection, or triage.",
		},
		{
			title: "Connect your tools",
			description: "Scoped OAuth. Only the apps this workflow needs.",
		},
		{
			title: "Write a task brief",
			description: "Inputs, thresholds, output format, escalation rules.",
		},
		{
			title: "Review first runs",
			description: "Use run logs to tune thresholds and lock in quality.",
		},
	],
	useCases: [
		{
			title: "Budget risk monitor",
			scenario: "Early warning before spend drift hurts CPA.",
			exampleTask:
				"Monitor Google Ads daily. Flag campaigns where spend rises 20%+ while conversions fall. Slack summary with causes and next actions.",
			outcome: "Catch risk early. Review only what matters.",
		},
		{
			title: "Weekly leadership brief",
			scenario: "One consistent cross-channel update every Monday.",
			exampleTask:
				"Every Monday 8am: compile PPC, SEO, and email performance. Top wins, top risks, three priority decisions.",
			outcome: "Meetings start with decisions, not data cleanup.",
		},
		{
			title: "Inbound task triage",
			scenario: "Requests arrive by email and get lost.",
			exampleTask:
				"Parse new inbound tasks, classify urgency, create structured tasks, notify owners when action needed within 24h.",
			outcome: "Requests captured and routed without manual sorting.",
		},
	],
	deepDiveTitle: "Implementation details",
	deepDiveIntro: "Full breakdown — reliability patterns, governance, and operational depth.",
	finalCtaTitle: "Ready to ship your first teammate?",
	finalCtaBody: "Start narrow. Prove it works. Then scale.",
	finalPrimaryCta: { label: "Start building", to: "/signup" },
	finalSecondaryCta: { label: "See pricing", to: "/pricing" },
};

/* ─── Marketing hub: /ai-teammates/marketing ─── */

export const AI_TEAMMATES_MARKETING_LANDING_UI: SeoLandingUiConfig = {
	articleLabel: "AI teammates marketing SEO page",
	breadcrumbs: [
		{ label: "Home", to: "/" },
		{ label: "AI Teammates", to: "/ai-teammates" },
		{ label: "Marketing", to: "/ai-teammates/marketing" },
	],
	integrationLogos: [
		"google-ads",
		"google-search-console",
		"ahrefs",
		"brevo",
		"klaviyo",
		"mailchimp",
		"adrapid",
		"google-sheets",
	],
	deepDiveCollapsedAfter: 2,
	hero: {
		kicker: "Marketing operations",
		subhead:
			"SEO, PPC, Search Console, and lifecycle workflows — running as one system, not scattered tabs.",
		proofPoints: [
			"Cross-channel in one cadence",
			"Staged by business impact",
			"Reports built for decisions",
		],
		primaryCta: { label: "Start free", to: "/signup" },
		secondaryCta: { label: "See Google Ads workflows", to: "/ai-teammates/marketing/google-ads" },
	},
	contextualCtas: [
		{
			id: "marketing-value-launch",
			afterSectionId: "value-props",
			label: "Launch marketing teammate",
			to: "/signup",
			supportingText: "Start with one channel outcome. Expand after quality is proven.",
		},
		{
			id: "marketing-use-case-google-ads",
			afterSectionId: "real-examples",
			label: "See Google Ads details",
			to: "/ai-teammates/marketing/google-ads",
			variant: "outline",
			supportingText: "Campaign-level controls, anomaly escalation, weekly optimization prep.",
		},
		{
			id: "marketing-deep-dive-start",
			afterSectionId: "deep-dive",
			label: "Get started",
			to: "/signup",
			supportingText: "Apply these patterns to your marketing stack today.",
		},
	],
	sectionIntros: [
		{
			sectionId: "value-props",
			title: "Marketing that runs itself",
			body: "One operating rhythm across paid, organic, and lifecycle. Not three disconnected dashboards.",
		},
		{
			sectionId: "onboarding",
			title: "Rollout in 4 steps",
			body: "One outcome first. Core signals. Proven quality. Then expand.",
		},
		{
			sectionId: "channel-module",
			title: "Cross-channel operating model",
			body: "Every channel shares one reporting language and one review loop.",
		},
		{
			sectionId: "real-examples",
			title: "Marketing use cases",
			body: "Real workflows you can deploy this week.",
		},
		{
			sectionId: "deep-dive",
			title: "Implementation depth",
			body: "From pilot to production — step by step.",
		},
		{
			sectionId: "backlinks",
			title: "Learn more",
			body: "Related pages and references.",
		},
		{
			sectionId: "faq",
			title: "FAQ",
			body: "Practical answers on integrations, monitoring, and rollout.",
		},
		{
			sectionId: "final-cta",
			title: "Turn marketing ops into a system",
			body: "One workflow. Prove quality. Then scale with confidence.",
		},
	],
	pageModule: {
		title: "Cross-channel operating model",
		body: "Every channel shares one reporting language and one review loop.",
		cards: [
			{
				title: "Integration sequencing",
				body: "High-signal sources first. Adjacent tools by measured impact.",
				bullets: [
					"Start with Google Ads + Search Console",
					"Add lifecycle tools by measurable ROI",
					"Avoid sprawl in phase one",
				],
			},
			{
				title: "Shared reporting",
				body: "One structure: wins, risks, causes, next actions.",
				bullets: [
					"Comparable output across channels",
					"Priority-first framing",
					"Less time on data cleanup",
				],
			},
			{
				title: "Clear ownership",
				body: "Every workflow has one owner, one escalation path, one review loop.",
				bullets: [
					"Named owner per automation",
					"Explicit escalation thresholds",
					"Single weekly calibration",
				],
			},
		],
	},
	valueCards: [
		{
			title: "One operating rhythm",
			description:
				"Paid, organic, and lifecycle execution unified. One cadence, not disconnected reports.",
		},
		{
			title: "Integrations staged by impact",
			description: "Connect what each workflow needs. Layer more tools after quality is proven.",
		},
		{
			title: "Reports built for action",
			description:
				"Consistent updates that start meetings with priorities, not spreadsheet cleanup.",
		},
	],
	steps: [
		{
			title: "Pick one acquisition outcome",
			description: "One measurable marketing objective before launching automation.",
		},
		{
			title: "Connect core signals",
			description: "Google Ads, Search Console, then layer adjacent tools deliberately.",
		},
		{
			title: "Standardize output",
			description: "Fixed summary: notable changes, likely causes, next actions.",
		},
		{
			title: "Scale by owner and impact",
			description: "Only where ownership is clear and prior runs are stable.",
		},
	],
	onboardingSteps: [
		{
			title: "Pick one channel objective",
			description: "Paid efficiency, organic growth, or lifecycle retention.",
		},
		{
			title: "Connect core sources",
			description: "Google Ads, Search Console, then add Ahrefs or email tools by phase.",
		},
		{
			title: "One output template",
			description: "Notable changes, likely causes, action queue, accountable owner.",
		},
		{
			title: "Weekly review and expand",
			description: "Promote only proven workflows from pilot to production.",
		},
	],
	useCases: [
		{
			title: "Cross-channel weekly growth brief",
			scenario: "One update across paid, SEO, and lifecycle in under 10 minutes.",
			exampleTask:
				"Every Monday: cross-channel growth brief. PPC efficiency, Search Console movement, email lifecycle performance, plus next three actions.",
			outcome: "One decision-ready brief instead of three disconnected reports.",
		},
		{
			title: "Search demand to campaign test queue",
			scenario: "Route keyword opportunities into both content and paid testing.",
			exampleTask:
				"Weekly: combine Search Console + Keyword Planner. Cluster by intent, rank by opportunity, produce SEO and Google Ads test candidates.",
			outcome: "Shared opportunity backlog across channels.",
		},
		{
			title: "Lifecycle performance watchdog",
			scenario: "Faster visibility into email drop-offs and underperforming flows.",
			exampleTask:
				"Track Brevo, Klaviyo, or Mailchimp campaign metrics. Flag abnormal opens, clicks, and unsubscribes with segment-level context.",
			outcome: "Lifecycle issues escalated before they become revenue leaks.",
		},
	],
	deepDiveTitle: "Marketing implementation depth",
	deepDiveIntro: "Roll out channel workflows with clear controls and measurable outcomes.",
	finalCtaTitle: "Turn marketing ops into a system",
	finalCtaBody: "One workflow. Prove quality. Then scale with confidence.",
	finalPrimaryCta: { label: "Launch marketing teammate", to: "/signup" },
	finalSecondaryCta: { label: "View pricing", to: "/pricing" },
};

/* ─── Google Ads: /ai-teammates/marketing/google-ads ─── */

export const AI_TEAMMATES_MARKETING_GOOGLE_ADS_LANDING_UI: SeoLandingUiConfig = {
	articleLabel: "AI teammates marketing Google Ads SEO page",
	breadcrumbs: [
		{ label: "Home", to: "/" },
		{ label: "AI Teammates", to: "/ai-teammates" },
		{ label: "Marketing", to: "/ai-teammates/marketing" },
		{ label: "Google Ads", to: "/ai-teammates/marketing/google-ads" },
	],
	integrationLogos: ["google-ads", "google-sheets"],
	deepDiveCollapsedAfter: 2,
	hero: {
		kicker: "Google Ads operations",
		subhead:
			"Monitoring, anomaly detection, and optimization prep — running daily with human approval built in.",
		proofPoints: [
			"Catch spend drift early",
			"Escalate only what matters",
			"Approvals stay with humans",
		],
		primaryCta: { label: "Start free for Google Ads", to: "/signup" },
		secondaryCta: { label: "See SEO workflows", to: "/ai-teammates/marketing/seo" },
	},
	contextualCtas: [
		{
			id: "google-ads-value-launch",
			afterSectionId: "value-props",
			label: "Create Google Ads workflow",
			to: "/signup",
			supportingText: "One risk-monitoring workflow. Calibrate thresholds weekly.",
		},
		{
			id: "google-ads-use-case-seo",
			afterSectionId: "real-examples",
			label: "Connect paid + SEO",
			to: "/ai-teammates/marketing/seo",
			variant: "outline",
			supportingText: "Pair anomaly monitoring with Search Console movement.",
		},
		{
			id: "google-ads-deep-dive-start",
			afterSectionId: "deep-dive",
			label: "Deploy Google Ads teammate",
			to: "/signup",
			supportingText: "Use these patterns to harden guardrails and scale reliably.",
		},
	],
	sectionIntros: [
		{
			sectionId: "value-props",
			title: "Google Ads without the fire drills",
			body: "Daily monitoring that catches risk early. No more surprise overspend.",
		},
		{
			sectionId: "onboarding",
			title: "Rollout in 4 steps",
			body: "Risk bounds first. Then standardize outputs and escalation.",
		},
		{
			sectionId: "channel-module",
			title: "Spend and CPA guardrails",
			body: "Explicit thresholds and predictable escalation. Not an alert firehose.",
		},
		{
			sectionId: "real-examples",
			title: "Google Ads use cases",
			body: "Adapt thresholds by campaign type and priority.",
		},
		{
			sectionId: "deep-dive",
			title: "Google Ads deep dive",
			body: "Production-grade controls, review loops, and ROI tracking.",
		},
		{
			sectionId: "backlinks",
			title: "Learn more",
			body: "Related pages and references.",
		},
		{
			sectionId: "faq",
			title: "FAQ",
			body: "Thresholds, governance, and multi-account scaling.",
		},
		{
			sectionId: "final-cta",
			title: "Deploy your Google Ads teammate",
			body: "One workflow. Tighten weekly. Scale after it's reliable.",
		},
	],
	pageModule: {
		title: "Spend and CPA guardrails",
		body: "Explicit thresholds and predictable escalation. Not an alert firehose.",
		cards: [
			{
				title: "Threshold architecture",
				body: "Risk bounds by campaign type. Volatility in context.",
				bullets: [
					"Spend pace thresholds",
					"CPA and conversion guardrails",
					"Impression share alerts",
				],
			},
			{
				title: "Escalation discipline",
				body: "Only actionable conditions reach owners.",
				bullets: [
					"Suppress low-signal noise",
					"Probable causes with each alert",
					"Mapped to accountable owner",
				],
			},
			{
				title: "Weekly calibration",
				body: "Review false positives and misses to stabilize precision.",
				bullets: [
					"Track alert quality over time",
					"Adjust one threshold at a time",
					"Promote only proven workflows",
				],
			},
		],
	},
	valueCards: [
		{
			title: "Faster anomaly detection",
			description: "Catch spend and CPA shifts early. Act before losses compound.",
		},
		{
			title: "Bid and budget guardrails",
			description:
				"Humans approve. Teammates surface decision-ready options with confidence notes.",
		},
		{
			title: "Agency-level discipline at any size",
			description: "Structured reporting and escalation — single account or multi-account.",
		},
	],
	steps: [
		{
			title: "Define risk thresholds",
			description: "Spend volatility, conversion changes, pacing exceptions.",
		},
		{
			title: "Standardize output",
			description: "Top movers, probable causes, recommended next steps.",
		},
		{
			title: "Route only actionable alerts",
			description: "High-impact to owners. Low-signal suppressed.",
		},
		{
			title: "Connect paid + organic",
			description: "Feed Ads signals into SEO prioritization.",
		},
	],
	onboardingSteps: [
		{
			title: "Connect Google Ads (read-first)",
			description: "Monitor and report. Validate quality before enabling actions.",
		},
		{
			title: "Set campaign-level thresholds",
			description: "Spend, CPA, conversion, impression-share bounds by campaign type.",
		},
		{
			title: "Define escalation paths",
			description: "High-severity to the right owner. Action recs with confidence notes.",
		},
		{
			title: "Calibrate weekly",
			description:
				"Review alert quality. Tune thresholds until escalations are consistently useful.",
		},
	],
	useCases: [
		{
			title: "Multi-account anomaly watch",
			scenario: "Agency team monitoring multiple client accounts.",
			exampleTask:
				"Monitor all Google Ads accounts daily. Escalate spend and CPA anomalies above account-specific thresholds with campaign context.",
			outcome: "Catch high-risk issues early across all accounts.",
		},
		{
			title: "Search-term waste cleanup",
			scenario: "Budget lost to low-intent queries reviewed too late.",
			exampleTask:
				"Every weekday: summarize high-spend low-conversion query clusters. Recommend negative keywords with expected waste reduction.",
			outcome: "Focused cleanup queue instead of raw query exports.",
		},
		{
			title: "Weekly optimization prep",
			scenario: "Account leads need a brief before optimization calls.",
			exampleTask:
				"Friday brief: top movers, pacing risks, test performance, three prioritized decisions for next week.",
			outcome: "Meetings go from recap to action mode.",
		},
	],
	deepDiveTitle: "Google Ads operations deep dive",
	deepDiveIntro: "Production-grade Ads execution with strong governance.",
	finalCtaTitle: "Deploy your Google Ads teammate",
	finalCtaBody: "One workflow. Tighten weekly. Scale after it's reliable.",
	finalPrimaryCta: { label: "Create Google Ads workflow", to: "/signup" },
	finalSecondaryCta: { label: "See pricing", to: "/pricing" },
};

/* ─── SEO: /ai-teammates/marketing/seo ─── */

export const AI_TEAMMATES_MARKETING_SEO_LANDING_UI: SeoLandingUiConfig = {
	articleLabel: "AI teammates marketing SEO subpage",
	breadcrumbs: [
		{ label: "Home", to: "/" },
		{ label: "AI Teammates", to: "/ai-teammates" },
		{ label: "Marketing", to: "/ai-teammates/marketing" },
		{ label: "SEO", to: "/ai-teammates/marketing/seo" },
	],
	integrationLogos: ["google-search-console", "ahrefs", "google-sheets"],
	deepDiveCollapsedAfter: 2,
	hero: {
		kicker: "SEO operations",
		subhead:
			"Search Console monitoring, keyword clustering, and SEO reporting — running daily without manual drift.",
		proofPoints: [
			"Daily Search Console coverage",
			"Keywords ranked by business value",
			"Reports built for stakeholders",
		],
		primaryCta: { label: "Start free for SEO", to: "/signup" },
		secondaryCta: { label: "See Google Ads workflows", to: "/ai-teammates/marketing/google-ads" },
	},
	contextualCtas: [
		{
			id: "seo-value-launch",
			afterSectionId: "value-props",
			label: "Create SEO workflow",
			to: "/signup",
			supportingText: "One Search Console monitoring loop. Then layer opportunity analysis.",
		},
		{
			id: "seo-use-case-google-ads",
			afterSectionId: "real-examples",
			label: "Pair with Google Ads",
			to: "/ai-teammates/marketing/google-ads",
			variant: "outline",
			supportingText: "Connect organic insights with paid-search testing priorities.",
		},
		{
			id: "seo-deep-dive-start",
			afterSectionId: "deep-dive",
			label: "Launch SEO teammate",
			to: "/signup",
			supportingText: "Harden monitoring, reporting, and governance before scaling.",
		},
	],
	sectionIntros: [
		{
			sectionId: "value-props",
			title: "SEO that actually runs consistently",
			body: "Recurring monitoring and reporting that doesn't depend on who remembered to check.",
		},
		{
			sectionId: "onboarding",
			title: "Rollout in 4 steps",
			body: "Search Console first. Output structure. Expand after alert quality is stable.",
		},
		{
			sectionId: "channel-module",
			title: "Search Console monitoring loop",
			body: "Clear thresholds, ownership, and action routing.",
		},
		{
			sectionId: "real-examples",
			title: "SEO use cases",
			body: "Tune for your page groups, query clusters, and stakeholder cadence.",
		},
		{
			sectionId: "deep-dive",
			title: "SEO implementation details",
			body: "Production rules, quality controls, and sequencing.",
		},
		{
			sectionId: "backlinks",
			title: "Learn more",
			body: "Related pages and references.",
		},
		{
			sectionId: "faq",
			title: "FAQ",
			body: "Search Console, keyword prioritization, and governance.",
		},
		{
			sectionId: "final-cta",
			title: "Launch your SEO teammate",
			body: "Search Console monitoring first. Validate. Then expand.",
		},
	],
	pageModule: {
		title: "Search Console monitoring loop",
		body: "Clear thresholds, ownership, and action routing.",
		cards: [
			{
				title: "Signal detection",
				body: "Track page and query movement. Separate noise from risk.",
				bullets: [
					"Click and position variance thresholds",
					"Priority page-group monitoring",
					"Coverage and indexing escalation",
				],
			},
			{
				title: "Keyword opportunity ranking",
				body: "Search Console + Keyword Planner demand and CPC context.",
				bullets: [
					"Intent-based clustering",
					"Commercial value weighting",
					"Action-ready opportunity queue",
				],
			},
			{
				title: "Action routing",
				body: "Technical and editorial follow-up to the right owner.",
				bullets: [
					"Owner-based escalation paths",
					"Weekly decision summaries",
					"Calibration for false positives",
				],
			},
		],
	},
	valueCards: [
		{
			title: "Always-on Search Console monitoring",
			description: "Page and query movement tracked continuously. Ranking shifts caught early.",
		},
		{
			title: "Keyword demand intelligence",
			description:
				"Keyword Planner + Search Console signals. Prioritized by intent and business impact.",
		},
		{
			title: "Reporting built for decisions",
			description: "Repeatable SEO summaries: significance, likely causes, and next actions.",
		},
	],
	steps: [
		{
			title: "Start with Search Console",
			description: "One monitoring cadence with explicit thresholds and owner accountability.",
		},
		{
			title: "Layer Keyword Planner context",
			description: "Cluster opportunity themes by intent and commercial value.",
		},
		{
			title: "Enforce report quality",
			description: "Significance framing, likely causes, and recommended actions in every output.",
		},
		{
			title: "Connect with paid signals",
			description: "Shared insights to coordinate Ads tests and organic content.",
		},
	],
	onboardingSteps: [
		{
			title: "Connect Search Console first",
			description: "Page and query monitoring for high-signal operational alerts.",
		},
		{
			title: "Layer Keyword Planner context",
			description: "Enrich query movement with demand and CPC signals.",
		},
		{
			title: "One recurring output format",
			description: "Wins, drops, likely causes, and prioritized next actions.",
		},
		{
			title: "Weekly SEO operating review",
			description: "Tune thresholds, audit escalations, keep one owner accountable.",
		},
	],
	useCases: [
		{
			title: "Search Console drop triage",
			scenario: "High-value pages losing clicks need fast response.",
			exampleTask:
				"Monitor priority pages daily. Flag significant click or position drops with likely causes and follow-up actions for technical or editorial owners.",
			outcome: "SEO regressions investigated in days, not weeks.",
		},
		{
			title: "Keyword opportunity queue",
			scenario: "Repeatable backlog for new content and paid-search tests.",
			exampleTask:
				"Weekly: combine Keyword Planner + Search Console. Cluster by intent and value. Ranked queue for content briefs and ad tests.",
			outcome: "Roadmaps driven by demand signals, not intuition.",
		},
		{
			title: "SEO reporting for leadership",
			scenario: "Consistent summary that non-SEO stakeholders can act on.",
			exampleTask:
				"Every Monday: SEO operations brief. Notable movement, risk items, opportunity themes, three recommended actions.",
			outcome: "Clear priorities instead of raw SEO exports.",
		},
	],
	deepDiveTitle: "SEO implementation details",
	deepDiveIntro: "Reliable monitoring, reporting, and governance loops.",
	finalCtaTitle: "Launch your SEO teammate",
	finalCtaBody: "Search Console monitoring first. Validate. Then expand by impact.",
	finalPrimaryCta: { label: "Create SEO workflow", to: "/signup" },
	finalSecondaryCta: { label: "See pricing", to: "/pricing" },
};
